/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.98: Court Bundle history page. Lists every export job the
 * signed-in user has triggered in the last 30 days so they can
 * re-download a previously generated bundle without rebuilding it.
 *
 * The experimental React 19 `set-state-in-effect` rule mis-flags
 * effect-driven state hydration after a fetch (the standard React
 * idiom we use across the app). Disabled in this file to match the
 * convention in CourtBundle.jsx / other pages.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API, api, getToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Clock, Download, FileWarning, History as HistoryIcon, Loader2, RefreshCw, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { formatBytes } from "@/lib/courtBundleHelpers";

const STATUS_PILL = {
  ready: { label: "Ready", className: "bg-[#D6F0EF] text-[#0F5658] border-[#A7D8DA]" },
  running: { label: "Building", className: "bg-[#FFF8E8] text-[#5C4516] border-[#E6CFA2]" },
  queued: { label: "Queued", className: "bg-[#F2F5F6] text-[#3F595B] border-[#D9E1E3]" },
  failed: { label: "Failed", className: "bg-[#FBEFEE] text-[#A93A2F] border-[#E6BAB6]" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CourtBundleHistory() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get("/exports/jobs/list?days=30&limit=100");
      setJobs(r.data?.jobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load bundle history.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group rows by batch so multi-child exports surface together —
  // mirrors what the user sees in the combined email.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const j of jobs) {
      const key = j.batch_id || `solo:${j.id}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(j);
    }
    // Sort buckets by the most-recent created_at inside them
    return Array.from(buckets.values())
      .map((rows) => rows.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")))
      .sort((a, b) => (b[0].created_at || "").localeCompare(a[0].created_at || ""));
  }, [jobs]);

  const downloadJob = async (job) => {
    if (job.status !== "ready") {
      toast.error("This bundle isn't ready yet.");
      return;
    }
    setDownloadingId(job.id);
    try {
      const token = getToken();
      const resp = await fetch(`${API}/exports/jobs/${job.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `Download failed (${resp.status}).`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.filename || `court-bundle-${job.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started.");
    } catch (e) {
      toast.error(e.message || "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="court-bundle-history-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Records</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 inline-flex items-center gap-3">
          <HistoryIcon className="w-7 h-7 text-[#116B6E]" strokeWidth={1.5} />
          Court Bundle History
        </h1>
        <p className="text-[#6F828A] mt-2 text-base max-w-2xl">
          Your recent court bundle exports from the last 30 days. Re-download a previously generated PDF without rebuilding it. Bundles older than 30 days are automatically purged.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            asChild
            className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]"
            data-testid="history-back-button"
          >
            <Link to="/app/court-bundle">
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.6} />
              Back to Court Bundle
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
            data-testid="history-refresh-button"
          >
            {refreshing
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.8} />
              : <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.6} />}
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="history-loading-card">
          <CardContent className="p-8 text-center text-sm text-[#6F828A]">
            <Loader2 className="w-5 h-5 inline-block animate-spin mr-2 text-[#116B6E]" strokeWidth={1.8} />
            Loading your bundle history…
          </CardContent>
        </Card>
      ) : jobs.length === 0 ? (
        <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="history-empty-card">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-9 h-9 mx-auto text-[#116B6E] mb-3" strokeWidth={1.5} />
            <div className="font-heading text-lg text-[#2E3A3F]">No bundles yet</div>
            <p className="text-sm text-[#6F828A] mt-2 max-w-md mx-auto">
              You haven't generated any court bundles in the last 30 days. When you do, they'll appear here so you can re-download them anytime.
            </p>
            <Button
              type="button"
              asChild
              className="mt-4 bg-[#116B6E] hover:bg-[#0F5658] text-white"
              data-testid="history-empty-cta"
            >
              <Link to="/app/court-bundle">Generate a court bundle</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="history-list">
          {grouped.map((rows) => {
            const head = rows[0];
            const isBatch = !!head.batch_id && rows.length > 1;
            return (
              <Card
                key={head.batch_id || head.id}
                className="border-[#D9E1E3] rounded-md shadow-none"
                data-testid={`history-group-${head.batch_id || head.id}`}
              >
                <CardHeader className="border-b border-[#D9E1E3] bg-[#FAFCFC]">
                  <CardTitle className="font-heading text-base inline-flex flex-wrap items-center gap-2">
                    <Clock className="w-4 h-4 text-[#116B6E]" strokeWidth={1.6} />
                    <span>{formatDate(head.created_at)}</span>
                    {isBatch && (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold text-[#0F5658] bg-[#D6F0EF] border border-[#A7D8DA] rounded-sm px-1.5 py-0.5">
                        Batch · {rows.length} {rows.length === 1 ? "bundle" : "bundles"}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ul className="divide-y divide-[#EEF3F4]">
                    {rows.map((job) => {
                      const pill = STATUS_PILL[job.status] || STATUS_PILL.queued;
                      const isDownloading = downloadingId === job.id;
                      return (
                        <li
                          key={job.id}
                          className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                          data-testid={`history-row-${job.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-[#2E3A3F]">
                                {job.label || job.filename || "Court bundle"}
                              </span>
                              <span
                                className={`inline-flex items-center text-[10px] uppercase tracking-[0.08em] font-semibold rounded-sm px-1.5 py-0.5 border ${pill.className}`}
                                data-testid={`history-status-${job.id}`}
                              >
                                {pill.label}
                              </span>
                              {job.status === "running" && (job.progress_pct != null) && (
                                <span className="text-[11px] text-[#6F828A] font-mono-data">
                                  {job.progress_pct}%
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-[#6F828A] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                              {job.filename && (
                                <span className="font-mono-data truncate" title={job.filename}>{job.filename}</span>
                              )}
                              {job.size_bytes ? <span>{formatBytes(job.size_bytes)}</span> : null}
                              {job.status === "ready" && job.ready_at && (
                                <span>Ready {formatDate(job.ready_at)}</span>
                              )}
                              {job.status === "failed" && (
                                <span className="text-[#A93A2F] inline-flex items-center gap-1">
                                  <FileWarning className="w-3 h-3" strokeWidth={1.6} />
                                  {(job.error || "Generation failed").slice(0, 140)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:flex-shrink-0">
                            {job.status === "ready" ? (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => downloadJob(job)}
                                disabled={isDownloading}
                                className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
                                data-testid={`history-download-${job.id}`}
                              >
                                {isDownloading ? (
                                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.8} /> Downloading…</>
                                ) : (
                                  <><Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} /> Download</>
                                )}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled
                                className="text-[#6F828A]"
                                data-testid={`history-disabled-${job.id}`}
                              >
                                {job.status === "failed" ? "Unavailable" : "Building…"}
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="text-xs text-[#6F828A] flex items-start gap-2 max-w-2xl leading-relaxed">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#116B6E]" strokeWidth={1.6} />
        <span>
          Bundle downloads require you to be signed in. PDFs are encrypted at rest and never shared outside your household — only you can re-download them from this page.
        </span>
      </div>
    </div>
  );
}
